import { Card, IconButton } from 'app-v2/design-systems'
import { Spacer } from 'app-v2/design-systems/components/Spacer/Spacer'
import { ORIENTATION, SIZE } from 'app-v2/design-tokens'
import { Layout } from 'app/common/layout'
import { H2, Text, fonts } from 'app/common/typography'
import handsBurger from 'shared/assets/images/hands-burger.png'
import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { colors } from 'shared/assets/styles'
import { NotFound } from 'app/not-found'
import { SuccessModal } from './components'
import { formatDateToHoursAndMinutes, getShortDate } from 'shared/utils'
import { Loader } from 'app/common/loader'
import { ErrorBanner } from 'app/ecommerce/common'
import {
    COMPLAINT_KEYS,
    useComplaint,
    usePostComplaint
} from 'app-v2/modules/complaint/useComplaint'
import { useQueryClient } from '@tanstack/react-query'
import {
    StyledBanner,
    StyledCaptionText,
    StyledCard,
    StyledConversationDiv,
    StyledDivider,
    StyledExchangeContainer,
    StyledIconContainer,
    StyledImage,
    StyledImageContainer,
    StyledLoaderContainer,
    StyledMessageContainer,
    StyledResponsiveContainer,
    StyledResponsiveContainerMessage,
    StyledTextContainer
} from './styles'
import { token, sender } from 'shared/utils/const'
import { NoComplaintScreen } from './components/NoComplaintScreen'
import { TextArea } from './components/TextArea'
import { Icon } from 'app-v2/assets/icons/Icon'
import { tokens } from 'app-v2/shared/features/icon/utils/tokens'

const DateDivider = ({ date }: { date: Date }) => {
    const intl = useIntl()

    return (
        <StyledDivider>
            <StyledCaptionText
                font={fonts.flameSansRegular}
                color={colors.bbqBrown}>
                <FormattedMessage
                    id="complaint.date"
                    values={{
                        date: getShortDate(date.toString(), intl),
                        hour: formatDateToHoursAndMinutes(date.toString())
                    }}
                />
            </StyledCaptionText>
        </StyledDivider>
    )
}

const Complaint = () => {
    const textAreaRef = useRef(null)
    const [customerMessage, setCustomerMessage] = useState('')
    const intl = useIntl()
    const searchParams = new URLSearchParams(window.location.search)
    const hasToken = searchParams.has(token)
    const tokenParams = searchParams.get(token)
    const queryClient = useQueryClient()
    const [loadingMessage, setLoadingMessage] = useState(false)
    const [openSuccessModal, setOpenSuccessModal] = useState(false)
    const [error, setError] = useState(false)
    const { data: complaint, isLoading, isError } = useComplaint(
        tokenParams ?? '',
        {
            enabled: !!tokenParams
        }
    )

    const { mutate: postComplaintMessage } = usePostComplaint({
        onSuccess: () => {
            setOpenSuccessModal(true)
            queryClient.invalidateQueries({
                queryKey: [COMPLAINT_KEYS.FETCH]
            })
            setCustomerMessage('')
            setLoadingMessage(false)
        },
        onError: error => {
            // eslint-disable-next-line no-console
            console.error(error)
            setError(true)
            setLoadingMessage(false)
        }
    })

    if (!hasToken) {
        return <NotFound />
    }

    const handleSubmit = () => {
        setError(null)
        if (tokenParams) {
            setLoadingMessage(true)
            const form = {
                token: tokenParams,
                message: customerMessage
            }
            postComplaintMessage(form)
        }
    }

    useEffect(() => {
        if (!isLoading && textAreaRef && !!textAreaRef.current) {
            textAreaRef.current.style.height = '0px'
            const scrollHeight = textAreaRef.current.scrollHeight
            // Trying to set this with state or a ref product an incorrect value.
            textAreaRef.current.style.height = scrollHeight + 'px'
        }
    }, [isLoading, textAreaRef, customerMessage])

    return (
        <>
            <Layout
                background="plain"
                border
                backgroundColor={colors.mayoEggWhite}>
                {isLoading ? (
                    <StyledLoaderContainer>
                        <Loader loading={isLoading} size={70} />
                    </StyledLoaderContainer>
                ) : isError ? (
                    <NoComplaintScreen />
                ) : (
                    complaint && (
                        <>
                            {!!error && (
                                <ErrorBanner
                                    isCart
                                    text={intl.formatMessage({
                                        id: 'complaint.errorBanner'
                                    })}
                                    onClose={() => setError(null)}
                                />
                            )}

                            <StyledBanner>
                                <StyledResponsiveContainer>
                                    <StyledTextContainer>
                                        <H2
                                            color={colors.mayoEggWhite}
                                            font={fonts.flameSansRegular}>
                                            <FormattedMessage
                                                id="complaint.reclamationNumber"
                                                values={{
                                                    complaintNumber:
                                                        complaint.complaintNumber ??
                                                        0
                                                }}
                                            />
                                        </H2>

                                        <Text
                                            font={fonts.flameSansRegular}
                                            color={colors.mayoEggWhite}>
                                            <FormattedMessage id="complaint.desc" />
                                        </Text>
                                    </StyledTextContainer>

                                    <StyledImageContainer>
                                        <StyledImage src={handsBurger} />
                                    </StyledImageContainer>
                                </StyledResponsiveContainer>
                            </StyledBanner>

                            <StyledResponsiveContainerMessage>
                                <StyledMessageContainer>
                                    <StyledExchangeContainer>
                                        {complaint.complaintStartDate && (
                                            <DateDivider
                                                date={
                                                    complaint.complaintStartDate
                                                }
                                            />
                                        )}
                                        <Spacer
                                            size={SIZE.S}
                                            orientation={ORIENTATION.VERTICAL}
                                        />

                                        {complaint.complaintDescription && (
                                            <StyledConversationDiv
                                                customer={sender.CLIENT}>
                                                <Text
                                                    font={
                                                        fonts.flameSansRegular
                                                    }>
                                                    {
                                                        complaint.complaintDescription
                                                    }
                                                </Text>
                                            </StyledConversationDiv>
                                        )}

                                        {complaint.messages.map(el => (
                                            <StyledConversationDiv
                                                key={el.date}
                                                customer={
                                                    el.sender === sender.CLIENT
                                                }>
                                                <Text
                                                    font={
                                                        fonts.flameSansRegular
                                                    }>
                                                    {el.message}
                                                </Text>
                                            </StyledConversationDiv>
                                        ))}
                                    </StyledExchangeContainer>
                                    <TextArea
                                        value={customerMessage}
                                        textAreaRef={textAreaRef}
                                        loading={loadingMessage}
                                        onChange={e =>
                                            setCustomerMessage(e.target.value)
                                        }
                                        onSubmit={handleSubmit}
                                        disabled={!customerMessage}
                                        disabledInput={!complaint.canAnswer}
                                    />
                                </StyledMessageContainer>
                                <StyledCard>
                                    <StyledIconContainer>
                                        <Icon
                                            code={tokens.circleWarning}
                                            color={colors.fieryRed}
                                        />
                                    </StyledIconContainer>
                                    <Text>
                                        {intl.formatMessage({
                                            id: 'complaint.warningData'
                                        })}
                                    </Text>
                                </StyledCard>
                            </StyledResponsiveContainerMessage>

                            {openSuccessModal && (
                                <SuccessModal
                                    onClose={() => setOpenSuccessModal(false)}
                                />
                            )}
                        </>
                    )
                )}
            </Layout>
        </>
    )
}

export { Complaint }
